import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import {
  Button,
  createStyles,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Radio,
  Theme,
  Typography,
  WithStyles,
  withStyles,
} from '@material-ui/core';
import { CheckCircle as CheckCircleIcon } from '@material-ui/icons';
import { actionChatActivityClose } from 'embeds/sagas/sagaWatchersChatActivity';
import { Field, Form, Formik } from 'formik';
import { find, forEach, get, head, map, sortBy, trim } from 'lodash';
import { ChatActivityReason, useChatActivityReasonMany } from 'requests/resources';

import useReduxMutation, { UseReduxMutationConfig } from 'hooks/useReduxMutation';

import { Dialog, DialogProps } from './Dialog';
import DialogTitle from './DialogTitle2';
import { RadioGroup } from './RadioGroup';
import { TextField } from './TextField';

const styles = ({
  custom: {
    palette: { colorSuccess },
  },
  palette: { text },
  spacing: { unit },
}: Theme) =>
  createStyles({
    button: {
      '&:last-child': {
        marginLeft: unit * 2,
      },
    },
    buttonLabel: {
      fontSize: 14,
      padding: `0px ${unit * 2}px`,
    },
    content: {
      minHeight: 268,
      padding: unit * 3,
    },
    footer: {
      textAlign: 'right',
    },
    form: {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
    },
    optionLabel: {
      color: text.primary,
      fontSize: '15px !important',
    },
    reason: {
      marginTop: unit * 2,
      paddingLeft: unit * 4,
    },
    successIcon: {
      color: colorSuccess,
      fontSize: 16,
      marginRight: unit,
    },
    text: {
      fontWeight: 500,
    },
    wrapper: {
      borderRadius: 0,
      width: 608,
    },
  });

const messages = defineMessages({
  title: {
    id: 'components.v2.DialogChatActivityClose.title',
    defaultMessage: 'Please select a reason for closing',
  },
  titleSuccess: {
    id: 'components.v2.DialogChatActivityClose.titleSuccess',
    defaultMessage: 'Chat was closed',
  },
  contentSuccess: {
    id: 'components.v2.DialogChatActivityClose.contentSuccess',
    defaultMessage:
      '{count, plural, one {Thanks, you successfully closed # message and submitted your reason for closing.} other {Thanks, you successfully closed # messages and submitted your reason for closing.}}',
  },
  actionClose: {
    id: 'components.v2.DialogChatActivityClose.actionClose',
    defaultMessage: 'Close',
  },
  actionCancel: {
    id: 'components.v2.DialogChatActivityClose.actionCancel',
    defaultMessage: 'Cancel',
  },
  actionSubmit: {
    id: 'components.v2.DialogChatActivityClose.actionSubmit',
    defaultMessage: 'Submit',
  },
});

const getAllowDescription = (chatActivityReasons: ChatActivityReason[], _id?: string) =>
  !!get(find(chatActivityReasons, { _id }), 'allowDescription');

type Values = {
  _id?: string;
};

export interface DialogChatActivityCloseProps
  extends WithStyles<typeof styles>,
    Omit<DialogProps, 'classes' | 'onClose'> {
  ids: string[];
  mutationConfig?: UseReduxMutationConfig;
  onClose: (success: boolean) => void;
}

/**
 * TODO Cleanup legacy code for `DialogChatActivityClose`
 *
 * @param props
 */
const DialogChatActivityClose: React.FC<DialogChatActivityCloseProps> = ({
  classes,
  ids,
  open,
  mutationConfig,
  onClose,
}) => {
  const { formatMessage } = useIntl();
  const { data: reasonsData } = useChatActivityReasonMany();
  const chatActivityReasons = sortBy(get(reasonsData, 'response'), 'allowDescription');
  const _id = get(head(chatActivityReasons), '_id');
  const [success, setSuccess] = useState(false);
  const [initialValues, setInitialValues] = useState({ _id });
  const [description, setDescription] = useState('');

  const [execute] = useReduxMutation(actionChatActivityClose);

  useEffect(() => {
    if (!initialValues._id && _id) setInitialValues({ _id });
  }, [initialValues._id, _id]);

  const handleFormSubmit = (values: Values) => {
    const data = {
      statusReason: values._id,
      description: getAllowDescription(chatActivityReasons, values._id) ? description : undefined,
    };
    // TODO replace by bulk action once BE is ready
    forEach(ids, (id) =>
      execute({
        method: 'put',
        url: `/chatActivities/v2/${id}/close`,
        data,
        ...mutationConfig,
        callbackSuccess: (response) => {
          setSuccess(true);

          if (mutationConfig && mutationConfig.callbackSuccess) {
            mutationConfig.callbackSuccess(response);
          }
        },
      }),
    );
  };

  return (
    <Formik initialValues={initialValues} enableReinitialize onSubmit={handleFormSubmit}>
      {(formikProps) => {
        const includeDescription = getAllowDescription(chatActivityReasons, formikProps.values._id);

        const handleClose = () => {
          onClose(success);
          setTimeout(() => {
            // Reset values on close using a timeout to skip flickering
            setSuccess(false);
            setDescription('');
            formikProps.resetForm();
          });
        };
        return (
          <Dialog open={open} fullWidth onClose={handleClose}>
            <DialogTitle onClose={handleClose}>
              {formatMessage(success ? messages.titleSuccess : messages.title)}
            </DialogTitle>
            <Form className={classes.form}>
              <DialogContent className={classes.content}>
                {success ? (
                  // DialogContent success
                  <Typography className={classes.text} color="primary" variant="body1">
                    <CheckCircleIcon className={classes.successIcon} />
                    {formatMessage(messages.contentSuccess, { count: ids.length })}
                  </Typography>
                ) : (
                  // DialogContent with form fields
                  <>
                    <Field name="_id">
                      {({ name, field }: any) => (
                        <RadioGroup name={name} {...field}>
                          {map(chatActivityReasons, (reason) => (
                            <FormControlLabel
                              classes={{ label: classes.optionLabel }}
                              key={reason._id}
                              value={reason._id}
                              label={reason.text.original}
                              control={
                                <Radio
                                  data-lh-id={`dialog-chat-activity-close-${reason.text.original}`}
                                  color="primary"
                                />
                              }
                            />
                          ))}
                        </RadioGroup>
                      )}
                    </Field>
                    {includeDescription && (
                      <TextField
                        className={classes.reason}
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                        multiline
                        rows={5}
                        fullWidth
                        data-lh-id="dialog-chat-activity-close-description"
                      />
                    )}
                  </>
                )}
              </DialogContent>
              {/* renderFooter */}
              {success ? (
                <DialogActions className={classes.footer} disableActionSpacing>
                  <Button
                    data-lh-id="button-dialog-chatactivity-close-close"
                    classes={{ root: classes.button, label: classes.buttonLabel }}
                    type="button"
                    variant="contained"
                    color="primary"
                    onClick={handleClose}
                  >
                    {formatMessage(messages.actionClose)}
                  </Button>
                </DialogActions>
              ) : (
                <DialogActions className={classes.footer} disableActionSpacing>
                  <Button
                    data-lh-id="button-dialog-chatactivity-close-cancel"
                    classes={{ root: classes.button, label: classes.buttonLabel }}
                    type="button"
                    color="primary"
                    onClick={handleClose}
                  >
                    {formatMessage(messages.actionCancel)}
                  </Button>
                  <Button
                    data-lh-id="button-dialog-chatactivity-close-submit"
                    classes={{ root: classes.button, label: classes.buttonLabel }}
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={formikProps.isSubmitting || (includeDescription && !trim(description))}
                  >
                    {formatMessage(messages.actionSubmit)}
                  </Button>
                </DialogActions>
              )}
            </Form>
          </Dialog>
        );
      }}
    </Formik>
  );
};

export default withStyles(styles)(DialogChatActivityClose);
